import React from 'react';
import { GuidanceTextResponse } from '../../utils';
import ImpactTable from '../../../../NPOFeedback/MakeEdits/components/ImpactTable';
import {
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
} from '../../../../NPOFeedback/utils';
import { parseImpactDetailsAnswer } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackImpactDetails';
import { useAllIndicatorsMin } from 'shared/apollo/pageHooks/useAllIndicators';
import { useAllServiceCollections } from 'shared/apollo/pageHooks/useAllServiceCollections';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { parseImpactSummaryAnswer } from '../../../../NPOFeedback/FeedbackDetails/ImpactSummaryDetails';
import {
  getIndicatorById,
  isCapacityDevelopmentEnabled,
} from '../../ImpactIndicator/utils';
import styled from 'styled-components';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { SERVICE_TYPE_QUESTION_ID } from '../../SectionMultiRadio';
import { Report } from 'shared/components/WelcomeBack/utils';
import { removeInvisible } from 'shared/utils/stringUtils';

interface ImpactFullModelProps {
  reportId: number | string;
  report: Report;
  programName: string;
  answers: {
    [id: number]: string;
  };
  guidanceText: GuidanceTextResponse;
  capacityDevelopmentEnabled: boolean | undefined;
  learnEnabled: boolean | undefined;
  actEnabled: boolean | undefined;
  reloadAnswers: () => unknown;
  handleBack: () => unknown;
  handleNext: () => unknown;
}

const hasChosenAnimal = (answers: ImpactFullModelProps['answers']): boolean => {
  const service = answers[SERVICE_TYPE_QUESTION_ID];
  if (!service) {
    return false;
  }
  return (
    service.toLowerCase().includes('animal') &&
    service.toLowerCase().includes('themselves')
  );
};

const StyledDiv = styled.div`
  position: relative;
  margin-top: ${SPACERS.extraLarge};
  .button-wrapper {
    position: sticky;
    width: 100%;
    left: 0;
    bottom: 0;
    display: block;
    margin-bottom: 0;
    padding-top: ${SPACERS.medium};
    padding-bottom: ${SPACERS.medium};
    background-color: ${COLORS.whiteBase};
    border-top: 1px solid ${COLORS.grayLight};
    button {
      margin-left: auto;
      margin-right: auto;
    }
    .validation-errors {
      margin-top: 0;
      margin-bottom: ${SPACERS.small};
    }
  }
`;

const ImpactFullModel: React.FC<ImpactFullModelProps> = ({
  report,
  reportId,
  programName,
  answers,
  guidanceText,
  capacityDevelopmentEnabled: capacityDevelopmentEnabledProp,
  learnEnabled: learnEnabledProp,
  actEnabled: actEnabledProp,
  reloadAnswers,
  handleBack,
  handleNext,
}) => {
  const {
    data: indicators,
    loading: indicatorsLoading,
  } = useAllIndicatorsMin();
  const {
    data: collections,
    loading: collectionsLoading,
  } = useAllServiceCollections({
    reportId,
  });

  if (indicatorsLoading || collectionsLoading) {
    return <TrueImpactLoading />;
  }

  const serviceAnswer = removeInvisible(answers[40002]);
  const serviceGoal = removeInvisible(answers[40003]);

  const learnEnabled =
    learnEnabledProp ??
    (hasChosenAnimal(answers) ? false : answers[40007] === 'Yes');
  const actEnabled =
    actEnabledProp ??
    (hasChosenAnimal(answers) ? false : answers[40008] === 'Yes');

  const capacityDevelopmentIndicators = answers[40021];
  const capacityDevelopmentEnabled =
    capacityDevelopmentEnabledProp ??
    isCapacityDevelopmentEnabled(capacityDevelopmentIndicators);

  const defaultAnswer = {
    act: [],
    capacityDevelopment: [],
    learn: [],
    catalyzed: [],
    reach: [],
    succeed: [],
  };

  const impactSummaryAnswer = answers[IMPACT_SUMMARY_QUESTION_ID];
  const impactDetailAnswer = answers[INDICATOR_DETAILS_QUESTION_ID];

  const impactDetails =
    parseImpactDetailsAnswer(impactDetailAnswer) || defaultAnswer;
  const impactSummary =
    parseImpactSummaryAnswer(impactSummaryAnswer) || defaultAnswer;

  const service = collections
    .find(c => c.name === serviceAnswer)
    ?.services.find(s => s.name === serviceGoal && s.version === 'v4');

  if (!service) {
    return (
      <TrueImpactText color="critical" align="center">
        Service not found. Please try refreshing the page.
      </TrueImpactText>
    );
  }

  const defaultIndicators = getIndicatorById(
    indicators,
    service.dbId,
    capacityDevelopmentEnabled,
    learnEnabled,
    actEnabled,
  );

  return (
    <StyledDiv>
      {/* <InfoModal /> */}
      <ImpactTable
        heading="Please check the summary and feel free to make any adjustments."
        nextButtonText="Save & Continue"
        reportId={Number(reportId)}
        service={service}
        feedback={[]}
        reportStage={report.stage}
        reportStatus={report.status}
        programName={programName}
        impactDetails={impactDetails}
        allIndicators={defaultIndicators}
        impactSummary={impactSummary}
        impactDetailAnswerId={undefined}
        impactSummaryAnswerId={undefined}
        handleClose={undefined}
        showErrorsInButtonWrapper={true}
        handleDone={handleNext}
        handleBack={handleBack}
        reloadAnswers={reloadAnswers}
      />
    </StyledDiv>
  );
};

export default ImpactFullModel;
